import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { ConfirmDialogHost } from '@/components/ConfirmDialog';
import { AppProvider, useApp } from '@/context';
import './App.css';

// Pages
import PricingPage from '@/pages/PricingPage';
import StorePage from '@/pages/StorePage';
import MyDeskeyPage from '@/pages/MyDeskeyPage';
import LicenceActivationPage from '@/pages/LicenceActivationPage';
import InstitutionDashboard from '@/pages/InstitutionDashboard';
import TenantLoginPage from '@/pages/TenantLoginPage';
import TeacherDashboard from '@/TeacherDashboard';
import TeacherRecordings from '@/TeacherRecordings';
import TeacherDrafts from '@/TeacherDrafts';
import TeacherQuizGenerator from '@/TeacherQuizGenerator';
import DepartmentDashboard from '@/DepartmentDashboard';
import StudentApp from '@/pages/student/StudentApp';
import StudentOnboarding from '@/pages/student/StudentOnboarding';
import SharedNarrationPage from '@/pages/SharedNarrationPage';
import PublicConsentPage from '@/pages/PublicConsentPage';
import PublicCreditConsentPage from '@/pages/PublicCreditConsentPage';
import MobileAppDownloadPage from '@/pages/MobileAppDownloadPage';
import AdminOverview from '@/AdminOverview';
import AdminTeachers from '@/AdminTeachers';
import AdminStudents from '@/AdminStudents';
import AdminClasses from '@/AdminClasses';
import AdminDevices from '@/AdminDevices';
import AdminBranding from '@/AdminBranding';
import AdminAISettings from '@/AdminAISettings';
import AdminBilling from '@/AdminBilling';
import AdminAnalytics from '@/AdminAnalytics';
import AdminAuditLogs from '@/AdminAuditLogs';
import SalesLogin from '@/pages/ops/OpsLogin';
import SalesApp from '@/pages/ops/OpsApp';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useApp();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        {/* The marketing landing page now lives in its own standalone project
            (../landing) — this app's root just opens the Institution/Teacher/
            Student login portal directly. */}
        <Route path="/" element={<MyDeskeyPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/store" element={<StorePage />} />
        <Route path="/mydeskey" element={<MyDeskeyPage />} />
        {/* Legacy /login route — keep alive as a 301-style redirect to the canonical /mydeskey portal (3-tab Institution / Teacher / Student). */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/activate-licence" element={<LicenceActivationPage />} />
        <Route path="/institution" element={<InstitutionDashboard />} />
        <Route path="/t/:slug" element={<TenantLoginPage />} />
        <Route path="/share/narrate/:token" element={<SharedNarrationPage />} />
        <Route path="/consent/:token" element={<PublicConsentPage />} />
        <Route path="/credit-consent/:token" element={<PublicCreditConsentPage />} />
        <Route path="/get-app/:role" element={<MobileAppDownloadPage />} />

        {/* Department Admin Routes */}
        <Route
          path="/department/*"
          element={
            <ProtectedRoute allowedRoles={['department_admin']}>
              <Routes>
                <Route index element={<DepartmentDashboard />} />
              </Routes>
            </ProtectedRoute>
          }
        />

        {/* Teacher Routes */}
        {/* More specific than /teacher/* below — React Router v6 ranks by
            specificity, not declaration order, so this always wins for
            this exact path regardless of where it's listed. */}
        <Route
          path="/teacher/quizzes"
          element={
            <ProtectedRoute allowedRoles={['teacher', 'tenant_admin', 'tenant_owner']}>
              <TeacherQuizGenerator />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher/*"
          element={
            <ProtectedRoute allowedRoles={['teacher', 'tenant_admin', 'tenant_owner']}>
              <TeacherDashboard />
            </ProtectedRoute>
          }
        />

        {/* Student Routes — MyDeskey Phase 1 (multi-institution + Netflix-style onboarding) */}
        <Route
          path="/student/onboarding"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentOnboarding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/*"
          element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentApp />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={['tenant_admin', 'tenant_owner', 'principal_or_director', 'academic_coordinator']}>
              <Routes>
                <Route index element={<AdminOverview />} />
                <Route path="overview" element={<AdminOverview />} />
                <Route path="teachers" element={<AdminTeachers />} />
                <Route path="students" element={<AdminStudents />} />
                <Route path="classes" element={<AdminClasses />} />
                <Route path="devices" element={<AdminDevices />} />
                <Route path="branding" element={<AdminBranding />} />
                <Route path="ai-settings" element={<AdminAISettings />} />
                <Route path="billing" element={<AdminBilling />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="audit-logs" element={<AdminAuditLogs />} />
              </Routes>
            </ProtectedRoute>
          }
        />

        {/* Platform Admin Routes */}
        <Route
          path="/platform/*"
          element={
            <ProtectedRoute allowedRoles={['platform_super_admin', 'support_admin', 'finance_admin']}>
              <Routes>
                <Route index element={<AdminOverview />} />
                <Route path="overview" element={<AdminOverview />} />
                <Route path="tenants" element={<AdminTeachers />} />
                <Route path="users" element={<AdminStudents />} />
                <Route path="billing" element={<AdminBilling />} />
                <Route path="ai-health" element={<AdminAISettings />} />
                <Route path="audit" element={<AdminAuditLogs />} />
                <Route path="settings" element={<AdminAISettings />} />
              </Routes>
            </ProtectedRoute>
          }
        />

        {/* Sales OS — dedicated portal (own JWT auth, separate from app users) */}
        <Route path="/sales/login" element={<SalesLogin />} />
        <Route path="/sales/*" element={<SalesApp />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="top-right" richColors />
      <ConfirmDialogHost />
    </BrowserRouter>
  );
}

function App() {
  return (
    <AppProvider>
      <AppRoutes />
    </AppProvider>
  );
}

export default App;
